<script setup lang="ts">
useHead({
  title: 'Terms of Service — Arvee Events',
  meta: [
    { name: 'robots', content: 'noindex, follow' },
    {
      name: 'description',
      content:
        'Terms of Service for Arvee Events, the threshold-based funding marketplace for community-driven RV events.',
    },
  ],
});

const config = useRuntimeConfig();

const portalTermsUrl = computed(() =>
  useCrossPropertyLink({
    url: `${config.public.portalUrl}/terms-of-service`,
    destination: 'portal',
    campaign: 'legal_link',
    placementId: 'terms_page',
  }),
);
</script>

<template>
  <div>
    <h1 class="text-h4 mb-2">Terms of Service</h1>
    <p class="text-body-2 text-medium-emphasis mb-6">
      Last updated: March 2026
    </p>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">1. Introduction</h2>
      <p class="text-body-1 mb-3">
        These terms govern your use of Arvee Events at events.arvee.io. Arvee
        Solutions, Inc. ("Arvee," "we," "us," or "our") operates this platform.
      </p>
      <p class="text-body-1 mb-3">
        Your use of Arvee Events is also subject to the general
        <a :href="portalTermsUrl" target="_blank" rel="noopener"
          >Arvee Terms of Service</a
        >. By using Arvee Events, you agree to both these terms and the general
        Arvee Terms of Service.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">2. The Arvee Events Marketplace</h2>
      <p class="text-body-1 mb-3">
        Arvee Events is a marketplace that facilitates the organization and
        discovery of community-driven events for the RV community. Events use a
        threshold-based funding model where events proceed only when enough
        participants commit.
      </p>
      <p class="text-body-1 mb-3">
        Arvee is solely a platform and marketplace facilitator. Arvee does
        <strong>not</strong>:
      </p>
      <ul class="text-body-1 mb-3 ml-6">
        <li>Organize, host, staff, supervise, or operate events</li>
        <li>Control event locations, activities, or logistics</li>
        <li>
          Guarantee the safety, quality, legality, or outcomes of any event
        </li>
        <li>Provide insurance, security, or on-site enforcement</li>
      </ul>
      <p class="text-body-1 mb-3">
        When you commit to an event as a participant, you are entering into a
        direct agreement with the event organizer. Arvee is not a party to that
        agreement. All obligations related to the event — including delivery,
        safety, quality, and compliance — are between you and the organizer.
      </p>
      <p class="text-body-1 mb-3">
        Arvee is not a bank, money transmitter, payment processor, broker,
        insurance provider, or financial services company. All payment
        processing is performed by Stripe, a licensed third-party payment
        processor.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">3. Organizer Responsibilities</h2>
      <p class="text-body-1 mb-3">
        Event organizers are solely responsible for planning and executing the
        event, ensuring legal compliance (permits, land access, local
        regulations), safety, risk management, and emergency planning, and the
        accuracy of event descriptions and expense summaries.
      </p>
      <p class="text-body-1 mb-3">
        Organizers are not agents, employees, or partners of Arvee. Organizers
        receive participant funds (minus service fees) and assume all real-world
        liability related to the event.
      </p>
      <p class="text-body-1 mb-3">
        Event organizers must accept the
        <NuxtLink to="/organizer-agreement">Organizer Agreement</NuxtLink>
        as a condition of listing events on Arvee Events. The Organizer
        Agreement governs the relationship between the organizer and Arvee,
        including payment processing, payout terms, and organizer obligations.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">4. Participant Responsibilities</h2>
      <p class="text-body-1 mb-3">
        Participants attend events at their own risk. Arvee does not vet,
        endorse, or supervise events. All event details are provided by the
        organizer and are not verified by Arvee.
      </p>
      <p class="text-body-1 mb-3">
        Committing to an event involves financial obligations — it authorizes a
        future charge if the event confirms. Participants are responsible for
        reviewing event details, understanding pricing and service fees, and
        complying with conduct rules.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">5. How Threshold-Based Funding Works</h2>
      <p class="text-body-1 mb-3">
        Events have a minimum participant threshold. During fundraising,
        participants commit with a payment method on file but are not charged
        immediately.
      </p>
      <p class="text-body-1 mb-3">
        After the commitment deadline, payment processing begins. If enough
        payments succeed to meet the threshold, the event confirms. If the
        threshold is not met, the event fails.
      </p>
      <p class="text-body-1 mb-3">
        Being charged during payment processing does <strong>not</strong> mean
        the event is confirmed.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">6. Fees and Payments</h2>
      <p class="text-body-1 mb-3">
        All payments are processed by Stripe. Arvee does not store raw card
        data.
      </p>
      <p class="text-body-1 mb-3">
        Service fees are charged when payment is captured and are non-refundable
        in all circumstances — including event failure, organizer cancellation,
        and force majeure. Service fees apply even to free-to-attend events.
      </p>
      <p class="text-body-1 mb-3">
        Organizers pay a listing bond when submitting an event for review. The
        listing bond is returned only upon event confirmation and is retained in
        all other outcomes (denial, abandonment, failure, cancellation).
      </p>
      <p class="text-body-1">
        See our
        <NuxtLink to="/refund-policy">Refund Policy</NuxtLink> for complete
        details.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">7. Organizer Payouts and Payment Processing</h2>
      <p class="text-body-1 mb-3">
        Organizers must complete Stripe Connect onboarding to receive event
        revenue. Arvee transfers funds to the organizer's connected Stripe
        account after event confirmation. Payout timing and mechanics are
        governed by Stripe's terms.
      </p>
      <p class="text-body-1 mb-3">
        Arvee acts solely as the limited payment agent of the organizer for
        purposes of accepting and processing payments from participants. Payment
        by a participant to Arvee satisfies the participant's payment obligation
        to the organizer. Arvee assumes no liability for any acts or omissions
        of the organizer.
      </p>
      <p class="text-body-1 mb-3">
        For event ticket purchases, the organizer is the merchant of record. The
        organizer's business name will appear on participant bank and credit
        card statements. Chargebacks and payment disputes related to event
        transactions are directed to the organizer's Stripe account.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">8. Code of Conduct</h2>
      <p class="text-body-1 mb-3">
        All users must comply with Arvee's platform code of conduct and any
        organizer-specific event rules.
      </p>
      <p class="text-body-1 mb-3">
        Arvee may remove or suspend events, suspend or ban users, withhold
        payouts, or reverse transactions in cases of fraud, abuse, or policy
        violations. All enforcement actions are logged.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">9. Limitation of Liability</h2>
      <p class="text-body-1 mb-3">
        To the fullest extent permitted by law, Arvee's total liability for any
        claim arising from or related to Arvee Events is limited to the service
        fees collected from you for the applicable event.
      </p>
      <p class="text-body-1 mb-3">Arvee is not liable for:</p>
      <ul class="text-body-1 mb-3 ml-6">
        <li>Personal injury or property damage</li>
        <li>Event cancellations or organizer misconduct</li>
        <li>Acts or omissions of participants or organizers</li>
        <li>The quality or safety of any event</li>
      </ul>
      <p class="text-body-1 mb-3">
        Force majeure events do not automatically guarantee refunds or limit
        Arvee's enforcement authority.
      </p>
      <p class="text-body-1 mb-3">
        In no event shall Arvee be liable for any indirect, incidental, special,
        consequential, or punitive damages, or any loss of profits or revenues,
        whether incurred directly or indirectly, or any loss of data, use,
        goodwill, or other intangible losses resulting from your use of Arvee
        Events.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">10. Indemnification</h2>
      <p class="text-body-1 mb-3">
        You agree to indemnify, defend, and hold harmless Arvee Solutions, Inc.,
        its officers, directors, employees, and agents from and against any
        claims, damages, losses, liabilities, and expenses (including reasonable
        attorneys' fees) arising from or related to your use of Arvee Events,
        your violation of these Terms, or your violation of any rights of a
        third party.
      </p>
      <p class="text-body-1 mb-3">
        If you are an event organizer, you additionally agree to indemnify,
        defend, and hold harmless Arvee from any claims, damages, losses, or
        expenses arising from or related to your event, including personal
        injury, property damage, regulatory violations, attendee disputes, and
        any failure to comply with applicable laws or regulations.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">11. Dispute Resolution</h2>

      <h3 class="text-h6 mb-2">Governing Law</h3>
      <p class="text-body-1 mb-3">
        These Terms are governed by and construed in accordance with the laws of
        the State of Delaware, without regard to its conflict of laws
        provisions.
      </p>

      <h3 class="text-h6 mb-2">Informal Resolution</h3>
      <p class="text-body-1 mb-3">
        Before filing a formal dispute, you agree to try to resolve the dispute
        informally by contacting us at
        <a href="mailto:support@arvee.io">support@arvee.io</a>. We will try to
        resolve the dispute informally within 30 days. If the dispute is not
        resolved within 30 days, either party may proceed as outlined below.
      </p>

      <h3 class="text-h6 mb-2">Mandatory Binding Arbitration</h3>
      <p class="text-body-1 mb-3">
        Any dispute, claim, or controversy arising out of or relating to these
        Terms or the use of Arvee Events that is not resolved through informal
        resolution shall be resolved by binding arbitration administered by the
        American Arbitration Association ("AAA") in accordance with its Consumer
        Arbitration Rules. The arbitration shall be conducted in the State of
        Delaware. The arbitrator's decision shall be final and binding and may
        be entered as a judgment in any court of competent jurisdiction.
      </p>

      <h3 class="text-h6 mb-2">Class Action Waiver</h3>
      <p class="text-body-1 mb-3">
        You agree that any dispute resolution proceedings will be conducted only
        on an individual basis and not in a class, consolidated, or
        representative action. You waive any right to participate in a class
        action lawsuit or class-wide arbitration against Arvee.
      </p>

      <h3 class="text-h6 mb-2">Small Claims Exception</h3>
      <p class="text-body-1 mb-3">
        Notwithstanding the above, either party may bring an individual action
        in small claims court for disputes within the court's jurisdictional
        limits.
      </p>

      <h3 class="text-h6 mb-2">Arbitration Opt-Out</h3>
      <p class="text-body-1 mb-3">
        You may opt out of the arbitration and class action waiver provisions by
        sending written notice to
        <a href="mailto:legal@arvee.io">legal@arvee.io</a> within 30 days of
        creating your account. Your notice must include your name, email
        address, and a clear statement that you wish to opt out of arbitration.
        If you opt out, disputes will be resolved in the state or federal courts
        located in the State of Delaware.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">12. Privacy</h2>
      <p class="text-body-1 mb-3">
        Your use of Arvee Events is also governed by our
        <NuxtLink to="/privacy">Privacy Policy</NuxtLink>.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">13. General Provisions</h2>
      <p class="text-body-1 mb-3">
        To the extent any provision of these Terms conflicts with mandatory
        consumer protection laws in your jurisdiction, the applicable law
        governs.
      </p>
      <p class="text-body-1 mb-3">
        If any provision of these Terms is found to be unenforceable, the
        remaining provisions will continue in full force and effect. Our failure
        to enforce any right or provision of these Terms will not be considered
        a waiver of those rights.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">14. Changes to These Terms</h2>
      <p class="text-body-1 mb-3">
        We may update these terms at any time. Continued use after changes
        constitutes acceptance.
      </p>
    </section>

    <section>
      <h2 class="text-h5 mb-3">15. Contact</h2>
      <p class="text-body-1 mb-3">
        Questions about these terms? Email us at
        <a href="mailto:support@arvee.io">support@arvee.io</a>.
      </p>
      <p class="text-body-1">
        Arvee Solutions, Inc.<br />
        2803 Philadelphia Pike Suite B-1290<br />
        Claymont, DE 19703
      </p>
    </section>
  </div>
</template>
